// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://bit.ly/CRA-PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

// The generated service worker (see build/service-worker.js) routes every
// navigation -- any URL without a file extension, i.e. every route this
// SPA's router handles -- through Workbox's registerNavigationRoute,
// which answers straight out of the Cache Storage precache for whichever
// index.html the *currently active* worker installed. That happens
// inside the service worker's own fetch handler, before the request ever
// reaches the network, so it never sees (and structurally can't see)
// server.js's Cache-Control: no-cache on '/'/'/index.html' -- those
// headers only affect requests that actually go over the wire, and a
// Workbox-intercepted navigation isn't one.
//
// SKIP_WAITING + clientsClaim() below (see registerValidSW) do get a new
// worker to take control of already-open tabs promptly -- but taking
// control doesn't retroactively change what's already rendered in a tab;
// only a fresh navigation does. Without reacting to that takeover, an
// already-open tab has no reliable way to ever pick up a deploy short of
// a manual reload racing (and sometimes losing to) the still-active old
// worker, or a full cache clear. reloadOnNewWorker fixes that: it reloads
// the page once a new worker actually takes control, deferring if the
// user looks mid-input (a focused text field) so a deploy landing while
// someone's filling out a form doesn't wipe what they've typed -- it
// retries the next time that field loses focus or the tab is
// backgrounded, rather than firing out from under them.
function reloadOnNewWorker() {
  let reloading = false;
  let waiting = false;

  const isEditingText = () => {
    const el = document.activeElement;
    if (!el) return false;
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  };

  const cleanup = () => {
    waiting = false;
    document.removeEventListener('focusout', deferredCheck, true);
    document.removeEventListener('visibilitychange', deferredCheck);
  };

  const maybeReload = () => {
    if (reloading) return;
    // Hidden means nothing is visibly interrupted even if some field
    // technically still holds focus, so it's always safe to reload then.
    if (document.visibilityState === 'hidden' || !isEditingText()) {
      reloading = true;
      cleanup();
      window.location.reload();
      return;
    }
    if (!waiting) {
      waiting = true;
      document.addEventListener('focusout', deferredCheck, true);
      document.addEventListener('visibilitychange', deferredCheck);
    }
  };

  // focusout fires before the next element actually receives focus --
  // give it a tick to settle before re-checking, so tabbing/clicking from
  // one field straight into another doesn't read as "done editing".
  const deferredCheck = () => setTimeout(maybeReload, 0);

  // Fires once per new worker taking control. The `reloading` guard is
  // what stops a second overlapping reload if this somehow ran twice
  // before the navigation actually started -- not a recurring loop: the
  // reload itself unloads this script, and a *later* deploy firing this
  // again is the intended behavior, not a bug.
  navigator.serviceWorker.addEventListener('controllerchange', maybeReload);
}

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    reloadOnNewWorker();

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://bit.ly/CRA-PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      // Check for a newer service worker whenever the tab regains focus,
      // instead of waiting for the browser's own (up to 24h) update check.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registration.update();
        }
      });

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // A new version has finished installing. Tell it to activate
              // immediately (skip waiting) instead of sitting idle until
              // every open tab is closed — the generated service worker
              // already listens for this message and calls clientsClaim(),
              // so the new version takes over in the background and the
              // next navigation/reload gets the fresh build.
              installingWorker.postMessage({ type: 'SKIP_WAITING' });

              console.log(
                'New content is available and will be used on the next ' +
                  'page load.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch(error => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then(response => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        'No internet connection found. App is running in offline mode.'
      );
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
      })
      .catch(error => {
        console.error(error.message);
      });
  }
}
